/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { CardWithProgress } from '@app/app/page-modules/resource/models';
import {
  ApprovalRequest,
  AssociateCategoryRequest,
  Audience,
  BrowseSettings,
  BrowseSettingsRequest,
  ContentUsage,
  DisplayConfigRequest,
  RegistrationSettings,
} from '../../models';
import { ObservableResult } from '../../store';
import { SharedCardDataService } from './card-data.service';

@Injectable()
export class MockSharedCardDataService implements SharedCardDataService {
  registerCardOpen(): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getCardDetails(cardUri: string): ObservableResult<CardWithProgress> {
    return ObservableResult.ofSuccess();
  }

  associateTag(resourceUid: string, tagUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateTag(resourceUid: string, tagUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateCardUri(resourceUid: string, newUri: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateAuthorDisplayConfig(resourceUid: string, request: DisplayConfigRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateBrowseSettings(resourceUid: string, request: BrowseSettingsRequest): ObservableResult<BrowseSettings> {
    return ObservableResult.ofSuccess({ ...request, updatedAt: new Date().toISOString() });
  }

  publishCard(resourceUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  unpublishCard(resourceUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  archiveCard(resourceUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateApprovalMethod(resourceUid: string, request: ApprovalRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getReviewsCount(cardId: string): ObservableResult<number> {
    return ObservableResult.ofSuccess();
  }

  associateReviewer(resourceUid: string, userUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateReviewer(resourceUid: string, userUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  associateCategory(cardUid: string, request: AssociateCategoryRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateCategory(cardUid: string, categoryTagUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  associateTargetGroup(cardId: string, groupUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateTargetGroup(cardId: string, groupUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  associateTargetUser(cardId: string, userUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateTargetUser(cardId: string, userUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateAudience(cardId: string, audience: Audience): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  setRegistration(cardId: string, settings: RegistrationSettings): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  clearRegistration(cardId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  getTargetingUsages(cardId: string): ObservableResult<ContentUsage[]> {
    return ObservableResult.ofSuccess([]);
  }

  updateTargetGroupSubscription(cardId: string, groupUid: string, force: boolean): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateAllTargetGroups(cardId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  associateTargetProgram(cardId: string, programUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  updateTargetProgramSubscription(cardId: string, programUid: string, force: boolean): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  dissociateTargetProgram(cardId: string, programUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
}
